'use client'

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ExternalLink, Globe, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface Client {
  name: string;
  description: string;
  category: string;
  url: string;
  image: string;
}

const clients: Client[] = [
  {
    name: "Journey Live Production",
    description: "African American theater company creating transformative productions that celebrate Black culture and history.",
    category: "Non-Profit",
    url: "https://www.journeyliveproduction.org/",
    image: "/work/journey-live.jpg"
  },
  {
    name: "Lonnie Ray Academy",
    description: "Elite youth basketball program combining athletic excellence with financial empowerment in the Quad Cities.",
    category: "Sports & Education",
    url: "https://lonnierayacademy.com/",
    image: "/work/lonnie-ray.jpg"
  },
  {
    name: "Alani Jones Foundation",
    description: "Supporting parents through pregnancy loss and stillbirth with compassionate care and community connection.",
    category: "Non-Profit",
    url: "https://www.alanijonesfoundation.org/",
    image: "/work/alani-foundation.jpg"
  },
  {
    name: "Harold's Chicken",
    description: "Chicago's legendary chicken restaurant with 70+ years of authentic flavors and community impact.",
    category: "Restaurant",
    url: "https://www.haroldschickensportsbar.com/",
    image: "/work/harolds-chicken.jpg"
  },
  {
    name: "Stitch Please",
    description: "Custom embroidery and personalized designs crafted with care for sports teams, schools, and events.",
    category: "Retail & Services",
    url: "https://www.stitchpleaseqc.com/",
    image: "/work/stitch-please.jpg"
  },
  {
    name: "Casa Grande Mexican Restaurant",
    description: "Authentic Mexican cuisine serving fresh, traditional dishes with vibrant flavors and warm hospitality.",
    category: "Restaurant",
    url: "https://www.casagrandemexicanrestaurantsnc.com/",
    image: "/work/casa-grande.jpg"
  }
];

export function ClientShowcase() {
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const generateScreenshotUrl = (url: string) => {
    if (!isClient) return "";
    // For Casa Grande specifically, use a different approach to ensure we get the top
    const isCasaGrande = url.includes('casagrandemexicanrestaurantsnc.com');
    const delay = isCasaGrande ? 3 : 2;
    
    if (isCasaGrande) {
      // Special parameters for Casa Grande to capture the top - minimal delay to catch it early
      return `https://api.screenshotone.com/take?access_key=FXXgAoXyM3QFSg&url=${encodeURIComponent(url)}&viewport_width=1280&viewport_height=800&device_scale_factor=1&format=png&image_quality=80&block_ads=true&block_cookie_banners=true&block_banners_by_heuristics=true&block_trackers=true&block_chats=true&delay=${delay}&timeout=20&full_page=false&viewport_mobile=false&wait_until=domcontentloaded`;
    }
    
    return `https://api.screenshotone.com/take?access_key=FXXgAoXyM3QFSg&url=${encodeURIComponent(url)}&viewport_width=1280&viewport_height=800&device_scale_factor=1&format=png&image_quality=80&block_ads=true&block_cookie_banners=true&block_banners_by_heuristics=true&block_trackers=true&block_chats=true&delay=${delay}&timeout=15&full_page=false&viewport_mobile=false&wait_until=domcontentloaded`;
  };
  
  const categories = ["All", ...Array.from(new Set(clients.map(client => client.category)))];
  const filteredClients = selectedCategory === "All" 
    ? clients 
    : clients.filter(client => client.category === selectedCategory);

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-[110rem] px-4 sm:px-6 lg:px-10">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <h2 className="text-2xl md:text-4xl font-bold mb-4 uppercase leading-tight" style={{ 
            color: 'rgb(29,29,31)', 
            fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif', 
            letterSpacing: '0.025em' 
          }}>
            <span className="font-light">Client</span>
            <span className="font-bold"> Showcase</span>
          </h2>
          <p className="text-base md:text-lg" style={{ color: 'rgba(0,0,0,0.88)' }}>
            Real websites we&apos;ve built for real businesses. Each project tells a story of growth, impact, and creative excellence.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={cn(
                "px-6 py-2 rounded-full text-sm font-medium transition-all duration-200",
                selectedCategory === category
                  ? "bg-[#171A20] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Client Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredClients.map((client) => (
            <div
              key={client.name}
              className="group relative bg-white rounded-2xl md:rounded-3xl overflow-hidden border-0 transition-all duration-300"
            >
                {/* Website Preview */}
                <div className="relative aspect-[16/10] bg-gray-50 overflow-hidden">
                  {/* Website Screenshot - only render when client-side */}
                  {isClient && generateScreenshotUrl(client.url) && (
                    <Image
                      src={generateScreenshotUrl(client.url)}
                      alt={`${client.name} website preview`}
                      fill
                      className="object-cover"
                      priority={false}
                      onError={(e) => {
                        // Fallback to placeholder if screenshot fails
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        const fallback = img.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  )}
                  
                  {/* Fallback preview - shown during SSR and on error */}
                  <div className={`absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center ${isClient && generateScreenshotUrl(client.url) ? 'hidden' : ''}`}>
                    <div className="text-center p-6">
                      <Globe className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h4 className="text-lg font-semibold text-gray-700 mb-2">{client.name}</h4>
                      <p className="text-sm text-gray-500 mb-3 break-all">{client.url}</p>
                      <div className="inline-flex items-center px-3 py-1 bg-white rounded-full text-xs font-medium text-gray-600">
                        Click to Visit
                      </div>
                    </div>
                  </div>
                  
                          {/* Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 via-black/30 to-transparent transition-all duration-300" />
                  
                  {/* Content Overlay */}
                  <div className="absolute inset-0 p-6 flex flex-col justify-between text-white">
                    <div className="flex justify-end items-start">
                      <Button asChild size="sm" className="bg-white text-[#171A20] hover:bg-white/90">
                        <Link href={client.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                          <ExternalLink className="h-4 w-4" />
                          Visit
                        </Link>
                      </Button>
                    </div>

                    <div className="mt-auto">
                      <h3 className="text-xl font-semibold leading-tight mb-2" style={{ color: 'rgb(255,255,255)' }}>
                        {client.name}
                      </h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.9)' }}>
                        {client.description}
                      </p>
                    </div>
                  </div>
                </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-20">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl md:text-4xl font-bold mb-6 uppercase leading-tight" style={{ 
              color: 'rgb(29,29,31)', 
              fontFamily: '"Universal Sans Display", -apple-system, Arial, sans-serif', 
              letterSpacing: '0.025em' 
            }}>
              <span className="font-light">Ready to join our</span>
              <span className="font-bold"> family?</span>
            </h3>
            <p className="text-base md:text-lg mb-8" style={{ color: 'rgba(0,0,0,0.88)' }}>
              Let&apos;s create something extraordinary together. Your vision, our expertise, unlimited possibilities.
            </p>
            <Button asChild size="lg" className="bg-[#171A20] text-white hover:bg-[#2a2d35]">
              <Link href="/contact" className="flex items-center gap-2">
                Start Your Project
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
